import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import automationService from '../../services/automationService';
import Layout from '../../components/Layout';
import EjarAccountCard from '../../components/EjarAccountCard';
import LoadingSpinner from '../../components/LoadingSpinner';
import Button from '../../ui/Button/Button';
import Card from '../../ui/Card';
import Badge from '../../ui/Badge';
import StatusBadge from '../../ui/Status/StatusBadge';
import Icon from '../../ui/Icon';
import { ICONS } from '../../ui/icon-map';
import { Input, Select } from '../../ui/Field/Field';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../ui/Table/Table';
import { CellMuted } from '../../ui/DataTable/cells';
import { mergeDocuments, toEjar, classify, blockers, notes, settled, documentKind } from './merge';
import Tabs from '../../ui/Tabs/Tabs';
import TarsRegisterTab from './TarsRegisterTab';
import EjarBookingTab from './EjarBookingTab';
import { usePermissions } from '../../hooks/usePermissions';
import { PERMISSIONS } from '../../constants/permissions';

// Scan the documents, check what was read, then one Register press (owner, 20 Oct 2026: one button, pressed once,
// no Prepare / Approve / Save; Ejar's screen is shown only when something went wrong; the TARS card is gone - the
// "Contract on TARS" tab reads its customer from Ejar). Images are sent to the engine, read in memory and dropped -
// none is stored, here or on the server.

const FIELDS = [
    ['given_names', 'Given names'],
    ['surname', 'Family name'],
    ['birth_date', 'Date of birth'],
    ['nationality', 'Nationality'],
    ['id_number', 'Identity number'],
    ['licence_number', 'Licence number'],
    ['expiry_date', 'Expiry date'],
    // a tourist's papers (owner, 10 Oct 2026): Ejar files the passport with its visa number
    ['document_number', 'Passport number'],
    ['visa_number', 'Visa number'],
    ['licence_country', 'Licence issued by'],
];

// Ejar's document types (Lookups/GetItemsByType): what Ejar itself offers - nothing here
// is invented, because an option that does not exist is worse than a free-text box.
const EJAR_WEB = 'https://ejarprod.iyelo.com:6300';

const EJAR_DOCUMENT_TYPES = [
    [250, 'Identity'], [251, 'Passport'], [252, 'Residence'],
    [253, 'Driver License'], [254, 'Work Card'], [255, 'Selfie'],
];

// Suggestions, not a closed list: the box stays free text, because a nationality the
// list forgot must never be a dead end.
const COUNTRY_HINTS = [
    'ARE', 'SAU', 'KWT', 'BHR', 'OMN', 'QAT',
    'EGY', 'PAK', 'IND', 'PHL', 'BGD', 'LKA', 'NPL', 'SYR', 'JOR', 'LBN', 'SDN', 'YEM',
    'GBR', 'USA', 'CAN', 'AUS', 'DEU', 'FRA', 'RUS', 'CHN', 'ZAF', 'NGA', 'KEN', 'ETH',
];

// Which boxes are a choice rather than free typing, per panel.
// Country, dialling code, and the ways somebody might search for it. Ejar's own dialog
// has the same two boxes; this one also answers to "egy", "eg" or "20".
const DIAL_COUNTRIES = [
    ['ARE', 'United Arab Emirates', '971'], ['SAU', 'Saudi Arabia', '966'],
    ['KWT', 'Kuwait', '965'], ['BHR', 'Bahrain', '973'], ['OMN', 'Oman', '968'],
    ['QAT', 'Qatar', '974'], ['EGY', 'Egypt', '20'], ['IND', 'India', '91'],
    ['PAK', 'Pakistan', '92'], ['PHL', 'Philippines', '63'], ['BGD', 'Bangladesh', '880'],
    ['LKA', 'Sri Lanka', '94'], ['NPL', 'Nepal', '977'], ['SDN', 'Sudan', '249'],
    ['JOR', 'Jordan', '962'], ['LBN', 'Lebanon', '961'], ['SYR', 'Syria', '963'],
    ['YEM', 'Yemen', '967'], ['MAR', 'Morocco', '212'], ['TUN', 'Tunisia', '216'],
    ['GBR', 'United Kingdom', '44'], ['USA', 'United States', '1'],
    ['CAN', 'Canada', '1'], ['RUS', 'Russia', '7'], ['FRA', 'France', '33'],
    ['DEU', 'Germany', '49'], ['ITA', 'Italy', '39'], ['ESP', 'Spain', '34'],
    ['TUR', 'Turkey', '90'], ['ZAF', 'South Africa', '27'],
];

const dialLabel = ([iso, name, dial]) => `${iso ? `${iso} · ` : ''}${name} (+${dial})`;

/**
 * Every country Ejar's own phone picker offers (engine lookups.phoneCountries, 246 of them),
 * the UAE first and the usual ones keeping their 3-letter code for searching. The short list
 * above is only the fallback until Ejar's list arrives: a Norwegian +47 could not be
 * entered while it was the only list (owner, 19 Oct 2026).
 */
const dialList = (ejarPhones) => {
    if (!ejarPhones?.length) return DIAL_COUNTRIES;
    const iso = new Map(DIAL_COUNTRIES.map((c) => [`${c[1].toLowerCase()}|${c[2]}`, c[0]]));
    const all = ejarPhones.map((p) => [iso.get(`${String(p.name).toLowerCase()}|${p.code}`) || '', p.name, p.code]);
    const home = all.findIndex((c) => c[2] === '971');
    return home > 0 ? [all[home], ...all.slice(0, home), ...all.slice(home + 1)] : all;
};

/** Whatever the employee typed, find the country they meant - or nothing. */
const findCountry = (typed, list = DIAL_COUNTRIES) => {
    const t = String(typed || '').trim().toLowerCase().replace(/^\+/, '');
    if (!t) return null;
    const exact = list.find((c) => dialLabel(c).toLowerCase() === t)
        || list.find((c) => (c[0] && c[0].toLowerCase() === t) || c[2] === t)
        || list.find((c) => c[1].toLowerCase() === t);
    if (exact) return exact;
    // A start of a name answers only when it names one country: with Ejar's 246, "n" or
    // "1" used to jump to the first match before "nor" or "1876" could be typed.
    const starts = list.filter((c) => (c[0] && c[0].toLowerCase().startsWith(t))
        || c[1].toLowerCase().startsWith(t));
    return starts.length === 1 ? starts[0] : null;
};

const CONTROLS = {
    gender: { kind: 'select', options: [['Male', 'Male'], ['Female', 'Female']] },
    phone: { kind: 'phone' },
    email: { kind: 'email' },
    document_type: { kind: 'select', options: EJAR_DOCUMENT_TYPES },
    nationality: { kind: 'country' },
    birth_date: { kind: 'date' },
    document_expiry: { kind: 'date' },
    document_issue: { kind: 'date' },
    licence_expiry: { kind: 'date' },
};

const SOURCE_STYLE = {
    mrz: ['machine zone', 'success'],
    printed: ['read off the card', 'warning'],
    manual: ['you typed it', 'info'],
    derived: ['concluded, not read', 'accent'],
};

// Section heading inside a card.
const TITLE = 'text-sm font-semibold text-gray-900';

const Pill = ({ tone = 'neutral', children, className = '' }) => (
    <Badge variant={tone} className={className}>{children}</Badge>
);

/** One merged field: the value, where it came from, and a box to correct it. */
const FieldRow = ({ name, label, cell, onChange }) => {
    const [tone, colour] = SOURCE_STYLE[cell?.source] || ['not read', 'neutral'];
    const id = `auto-field-${name}`;
    return (
        <div className="flex flex-wrap items-center gap-2 py-1.5 border-b border-gray-100 last:border-0">
            <label htmlFor={id} className="w-36 shrink-0 text-sm font-medium text-gray-700">{label}</label>
            <Input
                id={id}
                className="flex-1 min-w-[180px] font-mono"
                value={cell?.value || ''}
                placeholder="—"
                onChange={(e) => onChange(name, e.target.value)}
            />
            <Pill tone={colour}>{tone}</Pill>
            {cell?.agreed && <Pill tone="success">two documents agree</Pill>}
            {cell?.from && <span className="text-[11px] text-gray-600">{cell.from}</span>}
        </div>
    );
};

/**
 * What one uploaded file turned into.
 *
 * The warnings are the whole point of the row: a count alone ("4 warning(s)") tells
 * nobody why a real document failed, so the reasons open up in place, together with
 * the picture's size and the text the engine actually saw. Those three are what a bad
 * read is diagnosed from.
 */
const DocumentRow = ({ item }) => {
    const [open, setOpen] = useState(false);
    const r = item.result;
    const small = r && r.width > 0 && (r.width < 600 || r.height < 380);
    return (
        <Card padding="sm" className="text-sm">
            <div className="flex flex-wrap items-center gap-2">
                <Icon glyph={ICONS.document} size="sm" tone="muted" />
                <span className="font-semibold text-gray-900">{item.file.name}</span>
                <span className="text-gray-600">{Math.round(item.file.size / 1024)} KB</span>
                {r?.width > 0 && (
                    <Pill tone={small ? 'danger' : 'neutral'}>
                        {r.width}x{r.height}{small ? ' too small' : ''}
                    </Pill>
                )}
                {item.status === 'reading' && <Pill tone="info">reading…</Pill>}
                {item.status === 'failed' && <Pill tone="danger">{item.error}</Pill>}
                {r && (
                    <>
                        <Pill tone="neutral">{r.layout || 'layout not recognised'}</Pill>
                        {r.mrz
                            ? <Pill tone={r.mrz.valid ? 'success' : 'danger'}>
                                {r.mrz.format} {r.mrz.valid ? 'checks pass' : 'check FAILED'}
                            </Pill>
                            : <Pill tone="warning">no machine zone</Pill>}
                        {!!r.warnings?.length && (
                            <Button size="xs" variant="secondary" aria-expanded={open}
                                leadingIcon={<Icon glyph={ICONS.warning} size="xs" tone="warning" />}
                                onClick={() => setOpen((v) => !v)}>
                                {open ? 'hide' : 'why?'} · {r.warnings.length} warning(s)
                            </Button>
                        )}
                        <Button size="xs" variant="link" className="ms-auto" aria-expanded={open}
                            onClick={() => setOpen((v) => !v)}>
                            {open ? 'less' : 'details'}
                        </Button>
                    </>
                )}
            </div>

            {open && r && (
                <div className="mt-2 space-y-2 border-t border-gray-200 pt-2">
                    <ul className="list-disc ps-5 text-xs text-red-700">
                        {(r.warnings || []).map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                    {r.mrz && !r.mrz.valid && (
                        <div className="text-xs text-gray-800">
                            failed checks: <b>{Object.entries(r.mrz.checks || {})
                                .filter(([, ok]) => !ok).map(([k]) => k).join(', ') || '—'}</b>
                        </div>
                    )}
                    <div className="text-xs text-gray-600">
                        engine {r.engine} · {r.boxes} text boxes found
                    </div>
                    <div>
                        <div className="text-xs font-semibold text-gray-800">what the engine read:</div>
                        <pre className="mt-1 max-h-48 overflow-auto rounded border border-gray-200 bg-white p-2 text-[11px] leading-snug text-gray-900 whitespace-pre-wrap">
{r.text_sample || '(nothing)'}
                        </pre>
                    </div>
                </div>
            )}
        </Card>
    );
};

/**
 * What will be written, and the last place to change it.
 *
 * These two panels were read-only at first, which left the employee looking at a value
 * they could see was wrong with nothing to do about it. Every box is editable now, and
 * the fields with a known set of answers are a real choice rather than free typing.
 */
// What the employee types by hand on every customer goes first (owner, 20 Oct 2026); the rest is read off the papers.
const TYPED_FIRST = ['phone', 'email'];

const Panel = ({ title, note, values, edits, onChange, onReset, dial = DIAL_COUNTRIES }) => {
    const idOf = (key) => `auto-${title.toLowerCase()}-${key}`;
    const rank = (key) => (TYPED_FIRST.includes(key) ? TYPED_FIRST.indexOf(key) : TYPED_FIRST.length);
    const rows = Object.entries(values).sort(([a], [b]) => rank(a) - rank(b));
    return (
        <Card className="flex-1 min-w-0 sm:min-w-[300px]">
            <div className="mb-1 flex items-center gap-2">
                <h3 className={TITLE}>{title}</h3>
                {!!Object.keys(edits).length && (
                    <Button size="xs" variant="link" className="ms-auto" onClick={onReset}>undo my changes</Button>
                )}
            </div>
            <div className="mb-3 text-xs text-gray-600">{note}</div>

            <TableContainer>
            <Table>
                <TableBody>
                    {rows.map(([key, read]) => {
                        const edited = Object.prototype.hasOwnProperty.call(edits, key);
                        const value = edited ? edits[key] : (read === null ? '' : String(read));
                        const control = CONTROLS[key];
                        const id = idOf(key);
                        return (
                            <TableRow key={key}>
                                <TableCell className="w-40">
                                    <label htmlFor={id}>{key.replace(/_/g, ' ')}</label>
                                    {edited && <CellMuted tone="accent">edited</CellMuted>}
                                </TableCell>
                                <TableCell>
                                    {control?.kind === 'select' ? (
                                        <Select id={id} size="sm" className="w-full font-mono" value={value}
                                            onChange={(e) => onChange(key, e.target.value)}>
                                            <option value="">— choose —</option>
                                            {control.options.map(([v, labelText]) => (
                                                <option key={v} value={String(v)}>
                                                    {String(v) === labelText ? labelText : `${v} · ${labelText}`}
                                                </option>
                                            ))}
                                        </Select>
                                    ) : control?.kind === 'phone' ? (
                                        <div className="flex gap-2">
                                            {/* The fleet rents in Dubai, so this is the
                                                country nearly every time. A document that
                                                says otherwise still wins - this only
                                                fills the blank. */}
                                            <Input id={id} size="sm" className="w-1/2 font-mono" list="dial-countries"
                                                aria-label={`${key.replace(/_/g, ' ')}: country code`}
                                                placeholder="egy…"
                                                value={edits.__country ?? (edits.phone_code
                                                    ? (dial.find((c) => c[2] === String(edits.phone_code))
                                                        ? dialLabel(dial.find((c) => c[2] === String(edits.phone_code)))
                                                        : '')
                                                    : dialLabel(dial[0]))}
                                                onChange={(e) => {
                                                    const hit = findCountry(e.target.value, dial);
                                                    onChange('__country', hit ? dialLabel(hit) : e.target.value);
                                                    if (hit) onChange('phone_code', hit[2]);
                                                }} />
                                            <datalist id="dial-countries">
                                                {dial.map((c) => (
                                                    <option key={c[1] + c[2]} value={dialLabel(c)} />
                                                ))}
                                            </datalist>
                                            <Input size="sm" className="w-1/2 font-mono" type="tel"
                                                aria-label={`${key.replace(/_/g, ' ')}: number`}
                                                placeholder="553796104"
                                                value={edits.phone_number ?? ''}
                                                onChange={(e) => onChange('phone_number',
                                                    e.target.value.replace(/[^0-9]/g, ''))} />
                                        </div>
                                    ) : control?.kind === 'country' ? (
                                        <>
                                            <Input id={id} size="sm" className="w-full font-mono" list="country-hints" value={value}
                                                placeholder="e.g. SAU"
                                                onChange={(e) => onChange(key, e.target.value.toUpperCase())} />
                                            <datalist id="country-hints">
                                                {COUNTRY_HINTS.map((c) => <option key={c} value={c} />)}
                                            </datalist>
                                        </>
                                    ) : (
                                        <Input id={id} size="sm" className="w-full font-mono"
                                            type={['date', 'tel', 'email'].includes(control?.kind)
                                                ? control.kind : 'text'}
                                            value={value}
                                            placeholder={control?.kind === 'tel' ? '+9715…'
                                                : control?.kind === 'email' ? 'name@example.com' : '—'}
                                            onChange={(e) => onChange(key, e.target.value)} />
                                    )}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
            </TableContainer>
        </Card>
    );
};


/**
 * The TARS session, imported from the admin's own browser.
 *
 * TARS has no API key and no refresh token we could find, so the engine borrows the
 * admin's signed-in session the same way the Salik client already does. No password is
 * ever typed here, and the engine never hands the stored values back — only the cookie
 * names, so you can see what was pasted.
 *
 * The heartbeat behind this both keeps an idle session warm and records every probe, so
 * "aliveForHours" below is a measurement rather than a guess.
 */
const TarsSession = () => {
    // The engine signs in to TARS by itself - the RTA account kept in The Engine, the code RTA sends read from the
    // company Gmail (owner, 10 Oct 2026). Pasting a session copied from the browser's F12 is gone: "ده كده ملوش
    // لازمة". Signing in is admin-only on the engine.
    const { hasPermission } = usePermissions();
    const canConnect = hasPermission(PERMISSIONS.manage_automation_settings);
    const [status, setStatus] = useState(null);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState(null);

    const refresh = useCallback(() => {
        automationService.tarsStatus().then(setStatus).catch((e) => setMsg(e.message));
    }, []);
    useEffect(refresh, [refresh]);

    const run = async (fn, done) => {
        setBusy(true); setMsg(null);
        try { const r = await fn(); done?.(r); refresh(); }
        catch (e) { setMsg(e.message); }
        finally { setBusy(false); }
    };

    const session = status?.session;
    const life = status?.lifetime;
    const last = status?.recent?.[0];
    const live = last?.ok === 1;

    return (
        <Card>
            <div className="flex flex-wrap items-center gap-3">
                <h2 className={TITLE}>TARS session</h2>
                {!session ? <span className="inline-flex items-center gap-2 text-sm text-gray-600"><LoadingSpinner size="sm" />checking…</span>
                    : !session.present ? <StatusBadge status="missing" label="not signed in" />
                        : <>
                            <StatusBadge tone={live ? 'success' : 'danger'} label={live ? 'answering' : 'not answering'} />
                            <span className="text-xs text-gray-600">
                                signed in {session.importedAt}
                                {life?.aliveForHours != null && ` · alive ${life.aliveForHours} h`}
                                {life?.probes ? ` · ${life.probes} check(s)` : ''}
                            </span>
                        </>}
                <span className="ms-auto flex flex-wrap gap-2">
                    <Button size="sm" leadingIcon={<Icon glyph={ICONS.refresh} size="sm" />}
                        onClick={() => run(automationService.tarsProbe)} disabled={busy}>
                        Check now
                    </Button>
                    {canConnect && (
                        <Button size="sm" variant="primary" leadingIcon={<Icon glyph={ICONS.key} size="sm" />}
                            disabled={busy} isLoading={busy}
                            onClick={() => run(automationService.tarsSignIn, () => setMsg('signed in to TARS'))}>
                            {busy ? 'signing in…' : 'Sign in now'}
                        </Button>
                    )}
                </span>
            </div>
            <div className="mt-1 text-xs text-gray-600">
                The engine signs in by itself with the RTA account kept in The Engine; RTA&apos;s code is read from the
                company Gmail. It signs in again whenever the session ends.
            </div>

            {last && !live && (
                <div role="alert" className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    last check {last.at}: {last.detail}
                </div>
            )}
            {msg && <div role="status" className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">{msg}</div>}
        </Card>
    );
};

/**
 * Where the one Register press has got to (owner, 20 Oct 2026).
 *
 * While the engine works this is a line of text - what it is doing this second, so a long fill never looks like a
 * hang. Ejar's screen is shown only when something went wrong: then the picture is the quickest way to see why.
 */
const RegisterStatus = ({ run, onNext, onBook }) => {
    if (!run) return null;
    if (run.phase === 'working') {
        return (
            <div role="status" className="mt-4 flex flex-wrap items-center gap-2 rounded-lg border border-purple-200 bg-purple-50 px-4 py-3 text-sm text-gray-900">
                <LoadingSpinner size="sm" />
                <span className="font-semibold">{run.title}</span>
                {run.step && <span className="font-mono text-xs text-gray-600">{run.step}</span>}
            </div>
        );
    }
    if (run.phase === 'saved') {
        return (
            <div role="status" className="mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
                <span className="inline-flex items-center gap-2 font-semibold">
                    <Icon glyph={ICONS.success} size="sm" />{run.title}
                </span>
                {run.customerId && (
                    <>
                        <span>Ejar customer <span className="font-mono font-semibold">{run.customerId}</span></span>
                        <a href={`${EJAR_WEB}/app/crm/customers/view/${encodeURIComponent(run.customerId)}`}
                            target="_blank" rel="noopener noreferrer"
                            className="font-semibold text-purple-700 underline">open on Ejar</a>
                    </>
                )}
                {(onBook || onNext) && (
                    <span className="ms-auto inline-flex gap-2">
                        {/* the customer just saved, straight into a booking (owner, 21 Oct 2026) */}
                        {onBook && (
                            <Button size="sm" variant="primary" leadingIcon={<Icon glyph={ICONS.calendar} size="sm" />} onClick={onBook}>
                                Book on Ejar
                            </Button>
                        )}
                        {onNext && (
                            <Button size="sm" leadingIcon={<Icon glyph={ICONS.plus} size="sm" />} onClick={onNext}>
                                New customer
                            </Button>
                        )}
                    </span>
                )}
            </div>
        );
    }
    return (
        <Card className="mt-4 border-red-200">
            <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                <div className="font-semibold">{run.title}</div>
                {run.error && <div className="mt-1">{run.error}</div>}
                {!!run.left?.length && (
                    <>
                        <div className="mt-2 font-semibold">Ejar did not take:</div>
                        <ul className="list-disc ps-5">{run.left.map((x, i) => <li key={i}>{x}</li>)}</ul>
                    </>
                )}
            </div>
            {run.picture && (
                <img alt="Ejar's screen where it stopped" src={run.picture}
                    className="mt-3 w-full rounded-lg border border-gray-200" />
            )}
        </Card>
    );
};

/**
 * Before anything is filled (owner, 10 Oct 2026): "يبحث على العميل الاول ولو مش مسجل يسجله" and "يتوقع لو فى اى
 * اخطاء ... علشان ميوصلش للمرحلة الاخيرة ويقف". The customer is looked for on Ejar, and Ejar's own live rules are
 * applied to what was read - a customer already there, or anything Ejar would refuse, stops the fill here.
 */
const EjarCheck = ({ check, checking, onBook }) => {
    if (checking && !check) {
        return (
            <div role="status" className="mt-4 inline-flex items-center gap-2 text-sm text-gray-700">
                <LoadingSpinner size="sm" />looking for the customer on Ejar and checking Ejar&apos;s rules…
            </div>
        );
    }
    if (!check) return null;
    if (check.error) {
        return (
            <div role="status" className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900">
                Ejar could not be checked: {check.error}
            </div>
        );
    }
    const { existing, blockers: refused = [], warnings = [] } = check;
    return (
        <div className="mt-4 space-y-2">
            {existing ? (
                <div role="alert" className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
                    <div className="font-semibold">Already on Ejar - do not register again</div>
                    <div className="mt-1">
                        customer <span className="font-mono font-semibold">{existing.customer_id}</span>
                        {' · '}{existing.name || '—'}
                        {existing.nationality ? ` · ${existing.nationality}` : ''}
                        {' · '}found by <span className="font-mono">{existing.matched_on}</span>
                        {existing.has_contracts ? ' · has contracts' : ''}
                    </div>
                    {onBook && (
                        <Button size="sm" variant="primary" className="mt-2" leadingIcon={<Icon glyph={ICONS.calendar} size="sm" />} onClick={onBook}>
                            Book on Ejar for this customer
                        </Button>
                    )}
                </div>
            ) : check.searched === false ? null : (
                <div role="status" className="inline-flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-2 text-sm font-medium text-green-800">
                    <Icon glyph={ICONS.success} size="sm" />Not on Ejar yet - looked for by the passport / identity number
                </div>
            )}
            {!!refused.length && (
                <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800">
                    <div className="mb-1 font-semibold">Ejar would refuse this - fix it before filling:</div>
                    <ul className="list-disc ps-5">{refused.map((b, i) => <li key={i}>{b}</li>)}</ul>
                </div>
            )}
            {!!warnings.length && (
                <div role="status" className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900">
                    <ul className="list-disc ps-5">{warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
                </div>
            )}
            {!existing && !refused.length && (
                <div className="text-xs text-gray-600">nothing here that Ejar&apos;s own rules would refuse</div>
            )}
        </div>
    );
};

/**
 * A customer Ejar already has: what the new papers would change on his profile (owner, 12 Oct 2026 - "يقارن الاول
 * ويحدث ولو كارت العمل قديم يمسحه"). Only the papers running later than Ejar's change, an expired Work Card is deleted,
 * the rest stays; the engine fills Ejar's edit screen and the page saves it in the same press (owner, 20 Oct 2026).
 */
const EjarUpdatePlan = ({ plan, planning, onUpdate, updating }) => {
    if (planning && !plan) {
        return (
            <div role="status" className="mt-2 inline-flex items-center gap-2 text-sm text-gray-700">
                <LoadingSpinner size="sm" />comparing the new papers with the customer&apos;s Ejar profile…
            </div>
        );
    }
    if (!plan) return null;
    if (plan.error) {
        return (
            <div role="status" className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900">
                The Ejar profile could not be compared: {plan.error}
            </div>
        );
    }
    const rows = [
        ...plan.changes.map((c) => ({
            doc: c.type_label,
            onEjar: `${c.ejar_number} · to ${c.ejar_expiry || '—'}`,
            paper: `${c.number || c.ejar_number} · to ${c.expiry}`,
            action: <Badge variant="warning">{c.number ? 'new number, expiry and scan' : 'new expiry and scan'}</Badge>,
            expired: c.expired_on_ejar,
        })),
        ...plan.deletes.map((d) => ({
            doc: d.type_label,
            onEjar: `${d.number} · expired ${d.expiry}`,
            paper: '—',
            action: <Badge variant="danger">delete</Badge>,
            expired: true,
        })),
    ];
    return (
        <Card className="mt-3">
            <div className="mb-2 flex flex-wrap items-center gap-2">
                <h3 className={TITLE}>Update the Ejar profile from the new papers</h3>
                {plan.up_to_date
                    ? <StatusBadge tone="success" label="Ejar is up to date" />
                    : <StatusBadge tone="warning" label={`${rows.length} to change`} />}
            </div>
            {!!rows.length && (
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                {['#', 'Document', 'On Ejar', 'New paper', 'What happens'].map((h) => (
                                    <TableHeader key={h} align="center">{h}</TableHeader>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((r, i) => (
                                <TableRow key={`${r.doc}-${i}`}>
                                    <TableCell align="center">{i + 1}</TableCell>
                                    <TableCell align="center" className="font-semibold">{r.doc}</TableCell>
                                    <TableCell align="center" className={`font-mono ${r.expired ? 'text-red-700' : ''}`}>{r.onEjar}</TableCell>
                                    <TableCell align="center" className="font-mono">{r.paper}</TableCell>
                                    <TableCell align="center">{r.action}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            {!!plan.notes?.length && (
                <ul className="mt-2 list-disc ps-5 text-sm text-gray-700">
                    {plan.notes.map((n, i) => <li key={i}>{n}</li>)}
                </ul>
            )}
            {!plan.up_to_date && onUpdate && (
                <div className="mt-3 flex flex-wrap items-center gap-3">
                    <Button variant="primary" onClick={onUpdate} disabled={updating} isLoading={updating}
                        leadingIcon={<Icon glyph={ICONS.upload} size="sm" />}>
                        {updating ? 'updating Ejar…' : 'Update on Ejar'}
                    </Button>
                    <span className="text-xs text-gray-600">fills Ejar&apos;s edit screen and saves it - Ejar&apos;s record is read back after</span>
                </div>
            )}
        </Card>
    );
};

/**
 * Who registered whom today (owner, 20 Oct 2026), read from the row the engine writes on every Save. `tick` reloads it
 * after this page saves one.
 */
const TodayRegistrations = ({ tick }) => {
    const [data, setData] = useState(null);
    useEffect(() => {
        let alive = true;
        automationService.ejarRegistrations()
            .then((d) => { if (alive) setData(d); })
            .catch((e) => { if (alive) setData({ error: e.message, rows: [] }); });
        return () => { alive = false; };
    }, [tick]);
    if (!data) return null;
    const rows = data.rows || [];
    return (
        <Card>
            <div className="mb-2 flex flex-wrap items-center gap-2">
                <h2 className={TITLE}>Registered on Ejar today</h2>
                <Badge variant="neutral">{rows.length}</Badge>
                {data.error && <span className="text-xs text-red-700">could not be read: {data.error}</span>}
            </div>
            {rows.length ? (
                <TableContainer maxHeight="40vh">
                    <Table sticky>
                        <TableHead>
                            <TableRow>
                                {['#', 'Time', 'Customer', 'Ejar customer', 'Passport / ID', 'What', 'By'].map((h) => (
                                    <TableHeader key={h} align="center">{h}</TableHeader>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((r, i) => (
                                <TableRow key={`${r.at}-${i}`}>
                                    <TableCell align="center">{i + 1}</TableCell>
                                    <TableCell align="center" className="font-mono">{String(r.at || '').slice(11, 16)}</TableCell>
                                    <TableCell align="center" className="font-semibold">{r.customer_name || '—'}</TableCell>
                                    <TableCell align="center" className="font-mono">
                                        {r.customer_id ? (
                                            <a href={`${EJAR_WEB}/app/crm/customers/view/${encodeURIComponent(r.customer_id)}`}
                                                target="_blank" rel="noopener noreferrer"
                                                className="text-purple-700 underline">{r.customer_id}</a>
                                        ) : '—'}
                                    </TableCell>
                                    <TableCell align="center" className="font-mono">{r.identity || '—'}</TableCell>
                                    <TableCell align="center">
                                        <Badge variant={r.kind === 'update' ? 'info' : 'success'}>{r.kind === 'update' ? 'updated' : 'new'}</Badge>
                                    </TableCell>
                                    <TableCell align="center">{r.by || '—'}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : !data.error && <div className="text-center text-sm text-gray-600">nobody yet today</div>}
        </Card>
    );
};

/** What Ejar still needs before Register can go ahead - shown only when Ejar's own check finds something. */
const EjarNeeds = ({ result, onPickNationality, nationalities }) => {
    if (!result || result.ready) return null;
    const { resolved, unresolved = [], missing = [] } = result;
    return (
        <div role="alert" className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            <div className="mb-1 font-semibold">Ejar needs these before the customer can be registered:</div>
            <ul className="list-disc ps-5">
                {missing.map((m, i) => <li key={`m${i}`}>{m} is still empty</li>)}
                {unresolved.map((u, i) => <li key={`u${i}`}>{u}</li>)}
            </ul>
            {resolved?.nationality?.id == null && !!nationalities?.length && (
                <div className="mt-2 flex flex-wrap items-center gap-2 text-gray-900">
                    <label htmlFor="auto-ejar-nationality" className="text-xs">pick the nationality from Ejar&apos;s own list:</label>
                    <Select id="auto-ejar-nationality" size="sm" className="w-auto"
                        defaultValue=""
                        onChange={(e) => onPickNationality(e.target.value)}>
                        <option value="">— choose —</option>
                        {nationalities.map((n) => (
                            <option key={n.id} value={n.label}>{n.label}</option>
                        ))}
                    </Select>
                </div>
            )}
        </div>
    );
};

export default function AutomationPage() {
    // reading documents is use_automation; opening a case and writing the customer to Ejar is its own permission
    // (owner, 3 Oct 2026: the Viewer role could save a customer in Ejar)
    const { hasPermission } = usePermissions();
    const canSaveOnEjar = hasPermission(PERMISSIONS.save_customer_on_ejar);
    const canBookOnEjar = hasPermission('create_ejar_booking');          // the Ejar booking tab (21 Oct 2026)
    const [tab, setTab] = useState('customer');
    // Book on Ejar from a registration: the booking tab opens with this customer looked up (owner, 21 Oct 2026)
    const [bookingFor, setBookingFor] = useState(null);
    const [health, setHealth] = useState(null);
    const [items, setItems] = useState([]);          // {file, status, result, error}
    const [manual, setManual] = useState({});
    const [ejarEdits, setEjarEdits] = useState({});
    const [ejarPreview, setEjarPreview] = useState(null);
    const [ejarLists, setEjarLists] = useState(null);
    const [notice, setNotice] = useState(null);
    // the one Register press: {phase: working | saved | failed, title, step, error, left, picture}
    const [run, setRun] = useState(null);
    const [savedTick, setSavedTick] = useState(0);         // reloads "Registered on Ejar today"
    // a customer Ejar already has: what the new papers would change on his profile (owner, 12 Oct 2026)
    const [updatePlan, setUpdatePlan] = useState(null);
    const [planning, setPlanning] = useState(false);
    const [filling, setFilling] = useState(false);
    // The status appears below a long card, and an employee who cannot see it
    // thinks nothing happened. Bring it to them instead.
    const formPanelRef = useRef(null);
    const fileInput = useRef(null);

    useEffect(() => {
        automationService.health()
            .then(setHealth)
            .catch((e) => setNotice(e.message));
    }, []);

    // Ejar takes about half a minute to show anything after a page load. Start it now,
    // while the documents are still being read, so the button costs seconds later.
    useEffect(() => { if (canSaveOnEjar) automationService.ejarFormWarm(); }, [canSaveOnEjar]);

    // Ejar's own lists up front: the phone box offers every country Ejar's picker has.
    // Not reaching them is no stop - the short list still answers the usual codes.
    useEffect(() => {
        automationService.ejarLookups().then(setEjarLists).catch(() => {});
    }, []);
    const dial = useMemo(() => dialList(ejarLists?.phoneCountries), [ejarLists]);

    const readFiles = useCallback(async (files) => {
        const fresh = Array.from(files).map((file) => ({ file, status: 'reading' }));
        if (!fresh.length) return;
        setRun(null); setEjarPreview(null);            // a new paper is a new registration
        setItems((prev) => [...prev, ...fresh]);

        // One at a time: the engine reads a card in a few seconds, and a queue of
        // parallel uploads only makes the slowest one slower.
        for (const entry of fresh) {
            try {
                const result = await automationService.extract(entry.file);
                setItems((prev) => prev.map((x) => (x.file === entry.file
                    ? { ...x, status: 'done', result: { ...result, filename: entry.file.name } } : x)));
            } catch (e) {
                setItems((prev) => prev.map((x) => (x.file === entry.file
                    ? { ...x, status: 'failed', error: e.message } : x)));
            }
        }
    }, []);

    // nothing changes under a running Register: its papers and values are the ones Ejar is being filled with
    const onDrop = (e) => { e.preventDefault(); if (!filling) readFiles(e.dataTransfer.files); };

    const results = useMemo(() => items.filter((x) => x.result).map((x) => x.result), [items]);
    const merged = useMemo(() => mergeDocuments(results, manual), [results, manual]);
    // which papers were read: a passport with no identity card is a tourist (merge.classify)
    const kinds = useMemo(() => new Set(results.map(documentKind).filter(Boolean)), [results]);
    const kind = useMemo(() => classify(merged.fields, kinds), [merged.fields, kinds]);

    const ejarRead = useMemo(() => toEjar(merged.fields, kinds), [merged.fields, kinds]);

    // What would actually be written: the read, with the employee's corrections on top.
    const ejar = useMemo(() => {
        const merged = { ...ejarRead, ...ejarEdits };
        // The two boxes are the real answer; `phone` follows them so that the blocker
        // list and the preview never disagree about whether a phone was given.
        // A number typed without a country is a Dubai number: that is where the cars
        // are. Leaving the code blank used to leave the card saying "phone is still
        // empty" beside a phone the employee had just typed.
        if (merged.phone_number && !merged.phone_code) merged.phone_code = '971';
        if (merged.phone_code && merged.phone_number) {
            merged.phone = `+${merged.phone_code}${merged.phone_number}`;
        }
        return merged;
    }, [ejarRead, ejarEdits]);

    // Judged on what will be sent, not on what was read, so a box the employee has
    // just filled stops being listed as missing.
    // What the employee has answered themselves, in either place.
    const decided = useMemo(() => settled(manual, ejarEdits), [manual, ejarEdits]);
    const stop = useMemo(() => blockers(merged, ejar, true, decided), [merged, ejar, decided]);
    const remarks = useMemo(() => notes(merged, decided), [merged, decided]);

    const edit = (name, value) => setManual((prev) => ({ ...prev, [name]: value }));

    // The customer is looked for on Ejar as soon as a number is read, and Ejar's own rules are checked against what
    // will be sent - again whenever a number, a date or a document changes (owner, 10 Oct 2026).
    const [check, setCheck] = useState(null);
    const [checking, setChecking] = useState(false);
    const reading = items.some((x) => x.status === 'reading');
    const checkKey = JSON.stringify([ejar.identity_number, ejar.licence_number, ejar.visa_number, ejar.birth_date,
        ejar.document_type, ejar.document_expiry, ejar.licence_expiry, items.map((x) => x.file.name)]);
    useEffect(() => {
        setCheck(null);                        // a result for other numbers is never shown against these
        if (reading || !(ejar.identity_number || ejar.licence_number)) { setChecking(false); return undefined; }
        let alive = true;
        const wait = setTimeout(async () => {
            setChecking(true);
            try {
                const out = await automationService.ejarCheck(ejar, items.map((x) => x.file));
                if (alive) setCheck(out);
            } catch (e) {
                if (alive) setCheck({ error: e.message });
            } finally {
                if (alive) setChecking(false);
            }
        }, 700);
        return () => { alive = false; clearTimeout(wait); setChecking(false); };
    }, [checkKey, reading]); // eslint-disable-line react-hooks/exhaustive-deps
    // On Ejar already: compare the new papers with his profile (owner, 12 Oct 2026: "يقارن الاول ويحدث") - reads only.
    const existingId = check?.existing?.customer_id;
    useEffect(() => {
        setUpdatePlan(null);
        if (!existingId) { setPlanning(false); return undefined; }
        let alive = true;
        setPlanning(true);
        automationService.ejarUpdatePlan(ejar)
            .then((out) => { if (alive) setUpdatePlan(out); })
            .catch((e) => { if (alive) setUpdatePlan({ error: e.message }); })
            .finally(() => { if (alive) setPlanning(false); });
        return () => { alive = false; };
    }, [existingId, checkKey]); // eslint-disable-line react-hooks/exhaustive-deps
    // Ejar refuses a document without its picture, and the rows take the pictures in order: the filed one must be
    // among them (a licence alone would go on the passport row).
    const filedKind = kind.ejarDocumentType === 251 ? 'passport' : 'id';
    const filedPicture = results.some((r) => documentKind(r) === filedKind);
    const ejarBlocked = stop.length ? 'fix the red list above first'
        : check?.existing
        ? `already on Ejar as customer ${check.existing.customer_id} - not registered again`
        : check?.blockers?.length ? 'Ejar would refuse it - fix the red list first'
        : check?.error ? 'Ejar could not be checked - try again before filling'
        : !check ? 'checking Ejar first…'
        : (kind.known && !filedPicture)
            ? `add the ${filedKind === 'passport' ? 'passport' : 'identity card'} picture - Ejar refuses a document without it`
            : null;

    // Every scan the engine could read, because each document row wants its own. Ejar's rows take them in order -
    // the document filed first, the licence second - so the order is the documents', never the order they were
    // dropped in (a licence dropped first went on the passport row).
    const scansInOrder = () => {
        const filed = kind.ejarDocumentType === 251 ? 'passport' : 'id';
        const rank = (r) => { const k = documentKind(r); return k === filed ? 0 : k === 'licence' ? 1 : 2; };
        return items.filter((x) => x.result?.layout || x.result?.mrz)
            .sort((a, b) => rank(a.result) - rank(b.result))
            .map((x) => x.file);
    };

    // The next customer starts from an empty page (owner, 20 Oct 2026), rather than a reload.
    const newCustomer = () => {
        setItems([]); setManual({}); setEjarEdits({}); setRun(null); setEjarPreview(null); setNotice(null);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const picture = (job) => (job?.screenshot ? `data:image/png;base64,${job.screenshot}`
        : job?.preview ? `data:image/jpeg;base64,${job.preview}` : null);

    /**
     * One press, start to end (owner, 20 Oct 2026): fill Ejar's screen, and when every box took, save it. The engine
     * still checks it all again on the server (already on Ejar, still missing), and Ejar's own refusal after Save is
     * read back. Anything left unfilled is never saved: the job is cancelled and Ejar's screen is shown with why.
     */
    // the customer of this registration -> the Ejar booking tab, found there by his identity (or phone)
    const bookingKey = String(ejar?.identity_number || ejar?.phone || '').trim();
    const bookCustomer = canBookOnEjar && bookingKey
        ? () => {
            setBookingFor({ q: String(ejar?.identity_number || '').trim(), alt: String(ejar?.phone || '').trim(), at: Date.now() });
            setTab('booking');
        }
        : null;

    const runOnEjar = async (start, { working, saved }) => {
        setFilling(true); setNotice(null);
        setRun({ phase: 'working', title: working, step: 'opening Ejar…' });
        setTimeout(() => formPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
        let job = null;
        let saveSent = false;
        try {
            const started = await start();
            job = started;
            for (let i = 0; i < 200 && !['filled', 'failed', 'expired'].includes(job.state); i += 1) {
                await new Promise((r) => setTimeout(r, 1200));
                const seen = await automationService.ejarFormProgress(started.job);
                if (!seen) continue;
                job = { ...started, ...seen };
                setRun({ phase: 'working', title: working, step: job.step });
            }
            if (job.state === 'filled' && Array.isArray(job.left) && !job.left.length) {
                setRun({ phase: 'working', title: working, step: 'saving on Ejar…' });
                saveSent = true;
                const done = await automationService.ejarFormSave(started.job);
                if (done.state === 'saved') {
                    setRun({ phase: 'saved', title: saved, customerId: done.customer_id || null });
                    setSavedTick((t) => t + 1);
                    return;
                }
                // still pressing after the engine's wait: the end is not known, so nobody is told it failed
                setRun(done.state === 'saving'
                    ? { phase: 'failed', title: 'Save sent - the result is not known yet',
                        error: 'Ejar is slow to answer. Look at "Registered on Ejar today" or on Ejar before pressing again.' }
                    : { phase: 'failed', title: 'Not saved on Ejar', error: done.error || done.state, picture: picture(done) });
                setSavedTick((t) => t + 1);
                return;
            }
            // Something did not take, or the engine stopped: close Ejar's screen unsaved and show where it stopped.
            const closed = await automationService.ejarFormCancel(started.job).catch(() => null);
            setRun({
                phase: 'failed',
                title: 'Nothing was saved on Ejar',
                error: job.state === 'filled' ? null : (job.error || closed?.error || `the engine stopped (${job.state})`),
                left: job.left,
                picture: picture(closed) || picture(job),
            });
        } catch (e) {
            // once Save was sent, a lost answer does not mean a lost save
            setRun(saveSent
                ? { phase: 'failed', title: 'Save sent - the result is not known',
                    error: `${e.message}. Look at "Registered on Ejar today" or on Ejar before pressing again.` }
                : { phase: 'failed', title: 'Nothing was saved on Ejar', error: e.message, picture: picture(job) });
            if (saveSent) setSavedTick((t) => t + 1);
        } finally { setFilling(false); }
    };

    /**
     * Register: Ejar's own check of what will be sent first (ids for nationality, gender, document type). When Ejar
     * still needs something it is listed and nothing is opened; otherwise the screen is filled and saved.
     */
    const registerOnEjar = async () => {
        setFilling(true); setNotice(null); setRun(null); setEjarPreview(null);
        let result;
        try {
            const [preview, lists] = await Promise.all([
                automationService.ejarPreview(ejar),
                ejarLists ? Promise.resolve(ejarLists) : automationService.ejarLookups(),
            ]);
            result = preview;
            setEjarLists(lists);
        } catch (e) {
            setRun({ phase: 'failed', title: 'Nothing was saved on Ejar', error: e.message });
            setFilling(false);
            return;
        }
        if (!result.ready) { setEjarPreview(result); setFilling(false); return; }
        await runOnEjar(() => automationService.ejarFormStart(ejar, scansInOrder()), {
            working: 'Registering the customer on Ejar…',
            saved: `Customer registered on Ejar${ejar.first_name ? ` - ${[ejar.first_name, ejar.family_name].filter(Boolean).join(' ')}` : ''}`,
        });
    };

    // A customer Ejar already has: his edit screen filled with the new papers and saved in the same press
    // (owner, 12 Oct 2026; one press since 20 Oct 2026). The engine reads Ejar's record back after Save.
    const updateOnEjar = () => {
        // every paper the engine read, each with what it is: the engine puts each one on its own document
        const papers = items.filter((x) => x.result?.layout || x.result?.mrz)
            .map((x) => ({ file: x.file, kind: documentKind(x.result) }));
        return runOnEjar(() => automationService.ejarUpdateStart(ejar, papers), {
            working: "Updating the customer's Ejar profile…",
            saved: 'Customer updated on Ejar',
        });
    };

    const ocr = health?.ocr;

    return (
        <Layout title="Customer Automation" subtitle="Register the customer, the contract on TARS, its closing on TARS">
        {/* three jobs, one page (owner, 10 Oct 2026: "المفروض يكون فى تابات 1 تسجيل العميل 2 تسجيل العقد على تارز
            3 قفل العقد على تارز") */}
        <Tabs className="mb-4" label="Automation" idPrefix="auto" value={tab} onChange={setTab} items={[
            { id: 'customer', label: 'Customer registration' },
            { id: 'tars', label: 'Contract on TARS' },
            { id: 'close', label: 'Close on TARS' },
            ...(canBookOnEjar ? [{ id: 'booking', label: 'Ejar booking' }] : []),
        ]} />
        {tab === 'booking' && canBookOnEjar && (
            <div id="auto-panel-booking" role="tabpanel">
                <EjarBookingTab prefill={bookingFor} onPrefillUsed={() => setBookingFor(null)} />
            </div>
        )}
        {tab === 'tars' && <div id="auto-panel-tars" role="tabpanel" className="space-y-4"><TarsSession /><TarsRegisterTab /></div>}
        {tab === 'close' && <div id="auto-panel-close" role="tabpanel" className="space-y-4"><TarsSession /><TarsRegisterTab mode="close" /></div>}
        {tab === 'customer' && (
        <div id="auto-panel-customer" role="tabpanel" className="space-y-4">
            {/* saving a customer on Ejar is done under the employee's OWN Ejar login */}
            <EjarAccountCard />
            <Card>
                <div className="flex flex-wrap items-center gap-3">
                    <h2 className={TITLE}>Customer automation</h2>
                    {health?.reader === 'claude' ? (
                        <StatusBadge tone="success" label="Claude reads the documents" />
                    ) : ocr ? (
                        <>
                            <StatusBadge tone={ocr.usable ? 'success' : 'danger'}
                                label={ocr.usable ? `engine ready (tesseract ${ocr.binary})` : 'no OCR engine on the server'} />
                            {!!ocr.languages?.length && <span className="text-xs text-gray-600">languages: {ocr.languages.join(', ')}</span>}
                        </>
                    ) : <span className="inline-flex items-center gap-2 text-sm text-gray-600"><LoadingSpinner size="sm" />checking the engine…</span>}
                    <span className="ms-auto inline-flex items-center gap-1 text-xs text-gray-600">
                        <Icon glyph={ICONS.secure} size="xs" />
                        no document image is stored — it is read and dropped
                    </span>
                </div>
            </Card>


            {notice && (
                <div role="status" className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-900">
                    <Icon glyph={ICONS.info} size="sm" tone="primary" />
                    {notice}
                </div>
            )}

            <Card>
                <div
                    role="button"
                    tabIndex={0}
                    aria-label="Add the customer's documents"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={onDrop}
                    aria-disabled={filling}
                    onClick={() => { if (!filling) fileInput.current?.click(); }}
                    onKeyDown={(e) => {
                        if (!filling && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); fileInput.current?.click(); }
                    }}
                    className="cursor-pointer rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 px-6 py-8 text-center hover:border-purple-400 hover:bg-purple-50"
                >
                    <Icon glyph={ICONS.upload} size="lg" tone="primary" />
                    <div className="mt-2 text-base font-semibold text-gray-900">Drop the customer&apos;s documents here</div>
                    <div className="mt-1 text-sm text-gray-600">
                        identity card, driving licence, passport, entry permit — several at once is fine
                    </div>
                    <input
                        ref={fileInput}
                        type="file"
                        multiple
                        accept="image/*,application/pdf"
                        className="hidden"
                        onChange={(e) => { readFiles(e.target.files); e.target.value = ''; }}
                    />
                </div>

                {!!items.length && (
                    <div className="mt-3 space-y-2">
                        {items.map((item, i) => <DocumentRow key={`${item.file.name}-${i}`} item={item} />)}
                        <div className="flex justify-end">
                            <Button size="sm" variant="secondary" leadingIcon={<Icon glyph={ICONS.plus} size="sm" />}
                                onClick={newCustomer} disabled={filling}>
                                New customer
                            </Button>
                        </div>
                    </div>
                )}
            </Card>

            {/* Folded (owner, 20 Oct 2026): where each value came from is for checking a doubtful read; the Ejar boxes
                below are what the employee works with. */}
            {!!results.length && (
                <Card>
                    <details>
                        <summary className="flex cursor-pointer flex-wrap items-center gap-3">
                            <h2 className={TITLE}>What was read</h2>
                            {kind.known && (
                                <Pill tone="accent">
                                    {kind.tarsCustomerType} · Ejar {kind.ejarDocumentLabel} ({kind.ejarDocumentType})
                                </Pill>
                            )}
                            <span className="text-xs text-gray-600">open to see where each value came from, or to correct it</span>
                        </summary>
                        <div className="mt-2">
                            {FIELDS.map(([name, label]) => (
                                <FieldRow key={name} name={name} label={label} cell={merged.fields[name]} onChange={edit} />
                            ))}
                        </div>
                    </details>
                </Card>
            )}

            {!!merged.conflicts.length && (
                <Card className="border-red-200">
                    <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-red-700">
                        <Icon glyph={ICONS.warning} size="sm" />
                        The documents disagree
                    </h2>
                    {merged.conflicts.map((c, i) => (
                        <div key={i} className="py-1 text-sm text-gray-900">
                            <b>{c.field.replace(/_/g, ' ')}</b>: kept <span className="font-mono">{c.kept}</span> ({c.keptFrom})
                            {' '}· other <span className="font-mono">{c.other}</span> ({c.otherFrom})
                        </div>
                    ))}
                </Card>
            )}

            {!!results.length && (
                <Card>
                    <h2 className={`mb-3 ${TITLE}`}>
                        Ready for Ejar
                    </h2>
                    <fieldset disabled={filling} className="min-w-0">
                        <Panel title="Ejar"
                            note="customer profile + documents. Opening the contract stays with you."
                            values={ejarRead} edits={ejarEdits}
                            onChange={(k, v) => setEjarEdits((p) => ({ ...p, [k]: v }))}
                            onReset={() => setEjarEdits({})} dial={dial} />
                    </fieldset>

                    <div role="status" className={`mt-4 rounded-lg border px-4 py-3 text-sm ${stop.length
                        ? 'border-red-200 bg-red-50 text-red-800' : 'border-green-200 bg-green-50 text-green-800'}`}>
                        {stop.length ? (
                            <>
                                <div className="mb-1 font-semibold">Fix these before registering:</div>
                                <ul className="list-disc ps-5">
                                    {stop.map((s, i) => <li key={i}>{s}</li>)}
                                </ul>
                            </>
                        ) : <span className="inline-flex items-center gap-2 font-semibold"><Icon glyph={ICONS.success} size="sm" />Nothing is missing and nothing disagrees.</span>}
                    </div>

                    <EjarCheck check={check} checking={checking} onBook={bookCustomer} />
                    {!!check?.existing && (
                        <EjarUpdatePlan plan={updatePlan} planning={planning}
                            onUpdate={canSaveOnEjar ? updateOnEjar : null} updating={filling} />
                    )}

                    {/* The one button (owner, 20 Oct 2026): pressed once, it checks with Ejar, fills its screen and
                        saves. A customer Ejar already has is updated from the card above instead. */}
                    {!check?.existing && (
                        <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-gray-200 pt-3">
                            {canSaveOnEjar ? (
                                <Button variant="primary" leadingIcon={<Icon glyph={ICONS.save} size="sm" />}
                                    onClick={registerOnEjar} disabled={filling || !!ejarBlocked || run?.phase === 'saved'}
                                    isLoading={filling}>
                                    {filling ? 'registering…' : 'Register'}
                                </Button>
                            ) : <span className="text-xs font-medium text-gray-700">Registering on Ejar needs the &quot;save the customer in Ejar&quot; permission.</span>}
                            {canSaveOnEjar && !filling && run?.phase !== 'saved' && (
                                <span className="text-xs text-gray-600">
                                    {ejarBlocked || 'registers the customer on Ejar and saves - one press'}
                                </span>
                            )}
                        </div>
                    )}

                    <EjarNeeds result={ejarPreview} nationalities={ejarLists?.nationalities}
                        onPickNationality={(label) => {
                            setEjarEdits((p) => ({ ...p, nationality: label }));
                            setEjarPreview(null);
                        }} />

                    <div ref={formPanelRef}>
                        <RegisterStatus run={run} onNext={newCustomer} onBook={bookCustomer} />
                    </div>

                    {/* Notes, not blockers: what one document could not read is not a
                        problem once another document supplied it. */}
                    {!!remarks.length && (
                        <details className="mt-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-sm">
                            <summary className="cursor-pointer font-semibold text-gray-800">
                                {remarks.length} note(s) from the documents
                            </summary>
                            <ul className="mt-2 list-disc ps-5 text-gray-700">
                                {remarks.map((r, i) => (
                                    <li key={i}><span className="font-mono text-xs">{r.from}</span> — {r.text}</li>
                                ))}
                            </ul>
                        </details>
                    )}
                </Card>
            )}

            {canSaveOnEjar && <TodayRegistrations tick={savedTick} />}
        </div>
        )}
        </Layout>
    );
}
